import type {
  ContactScoreBreakdown,
  ContactScoreBreakdownMeta,
} from "@/hooks/use-crm-contact-score";
import { Skeleton } from "@/components/skeleton";

interface CrmLeadScoreEvidenceCardProps {
  breakdown: ContactScoreBreakdown | null;
  meta: ContactScoreBreakdownMeta | null;
  isLoading: boolean;
  error: string | null;
  fallbackScore: number | null;
  title?: string;
  description?: string;
}

export function CrmLeadScoreEvidenceCard({
  breakdown,
  meta,
  isLoading,
  error,
  fallbackScore,
  title = "Lead score evidence",
  description = "Explainable scoring from engagement, fit, and intent signals.",
}: CrmLeadScoreEvidenceCardProps) {
  const fallbackBreakdown = !isLoading && error && fallbackScore !== null
    ? buildStoredScoreFallback(fallbackScore)
    : null;
  const visibleBreakdown = breakdown ?? fallbackBreakdown;
  const categories = Array.isArray(visibleBreakdown?.categories) ? visibleBreakdown.categories : [];
  let degradedReason: string | null = null;
  if (fallbackBreakdown) {
    degradedReason = "Showing the stored lead score because score evidence is temporarily unavailable.";
  } else if (meta?.degraded) {
    degradedReason = formatScoreDegradedReason(meta.reason);
  }
  const visibleError = error && !visibleBreakdown ? error : null;

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        {visibleBreakdown ? (
          <span className="rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary-light)] px-2.5 py-1 text-xs font-semibold text-[var(--color-primary)]">
            {visibleBreakdown.totalScore} / 100 - {visibleBreakdown.tier}
          </span>
        ) : null}
      </div>
      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : visibleError ? (
        <p className="mt-3 text-sm text-[var(--color-destructive)]">{visibleError}</p>
      ) : visibleBreakdown ? (
        <div className="mt-3 space-y-3">
          {degradedReason ? (
            <p className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-3 py-2 text-xs text-[var(--color-warning)]">
              {degradedReason}
            </p>
          ) : null}
          <div className="grid gap-2 md:grid-cols-3">
            {categories.map((category, index) => {
              const categoryName = typeof category.name === "string" && category.name.trim()
                ? category.name
                : `category-${index + 1}`;
              const score = typeof category.score === "number" && Number.isFinite(category.score) ? category.score : 0;
              const maxScore = typeof category.maxScore === "number" && Number.isFinite(category.maxScore) && category.maxScore > 0
                ? category.maxScore
                : 1;
              const factors = Array.isArray(category.factors) ? category.factors : [];
              const visibleFactors = factors.slice(0, 3).map((factor, factorIndex) => {
                const label = typeof factor?.label === "string" && factor.label.trim()
                  ? factor.label.trim()
                  : factorIndex === 0
                    ? "Signal"
                    : `Signal ${factorIndex + 1}`;
                const evidence = typeof factor?.evidence === "string" && factor.evidence.trim()
                  ? factor.evidence.trim()
                  : "Evidence temporarily unavailable";
                const points = typeof factor?.points === "number" && Number.isFinite(factor.points)
                  ? factor.points
                  : null;

                return { label, evidence, points };
              });
              const hiddenFactorCount = Math.max(0, factors.length - visibleFactors.length);

              return (
                <div key={categoryName} className="rounded-lg bg-[var(--color-background)] px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase text-[var(--color-muted-foreground)]">
                      {formatScoreCategoryName(categoryName)}
                    </p>
                    <span className="text-xs font-semibold text-[var(--color-foreground)]">
                      {score}/{maxScore}
                    </span>
                  </div>
                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]"
                    role="progressbar"
                    aria-label={`${formatScoreCategoryName(categoryName)} score ${score} of ${maxScore}`}
                    aria-valuemin={0}
                    aria-valuemax={maxScore}
                    aria-valuenow={score}
                  >
                    <div
                      className="h-full rounded-full bg-[var(--color-primary)]"
                      style={{ width: `${Math.min(100, Math.round((score / maxScore) * 100))}%` }}
                    />
                  </div>
                  {visibleFactors.length > 0 ? (
                    <div className="mt-2 space-y-1.5">
                      {visibleFactors.map((factor, factorIndex) => (
                        <div key={`${categoryName}-${factor.label}-${factorIndex}`} className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-medium text-[var(--color-foreground)]">{factor.label}</p>
                            {factor.points !== null ? (
                              <span className="shrink-0 rounded-full bg-[var(--color-muted)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-muted-foreground)]">
                                {factor.points >= 0 ? "+" : ""}{factor.points} pts
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">{factor.evidence}</p>
                        </div>
                      ))}
                      {hiddenFactorCount > 0 ? (
                        <p className="text-xs text-[var(--color-muted-foreground)]">+{hiddenFactorCount} more scoring signals</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {visibleBreakdown.improvementSuggestion ? (
            <p className="rounded-lg border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-3 py-2 text-xs text-[var(--color-ai)]">
              {visibleBreakdown.improvementSuggestion}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No score evidence available yet.</p>
      )}
    </section>
  );
}

export function buildStoredScoreFallback(score: number): ContactScoreBreakdown {
  const safeScore = Math.max(0, Math.min(100, Math.round(score)));
  return {
    totalScore: safeScore,
    tier: safeScore >= 75 ? "Hot" : safeScore >= 40 ? "Warm" : "Cold",
    categories: [
      {
        name: "engagement",
        score: safeScore,
        maxScore: 100,
        factors: [{
          label: "Stored lead score",
          points: safeScore,
          evidence: "Live factor evidence is temporarily unavailable.",
        }],
      },
    ],
    improvementSuggestion: "Refresh score evidence before changing this contact's priority or routing.",
  };
}

export function formatScoreCategoryName(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function formatScoreDegradedReason(reason?: string) {
  if (reason === "activity_evidence_unavailable") {
    return "Showing the stored lead-score projection because activity evidence is temporarily unavailable.";
  }
  if (reason === "contact_projection_unavailable") {
    return "Showing a safe default score because the contact score projection is temporarily unavailable.";
  }
  if (reason === "score_computation_unavailable") {
    return "Showing the stored lead-score projection because factor-level scoring is temporarily unavailable.";
  }
  return "Showing degraded score evidence while the full scoring read model catches up.";
}
